/**
 * 新手引导流程优化
 * https://alidocs.dingtalk.com/i/nodes/YMyQA2dXW7rOo3Ozt9RpeK2w8zlwrZgb?doc_type=wiki_doc&rnd=0.7378994771518468
 */
import { computed, ref, readonly, type Ref, type ComputedRef, watchEffect, getCurrentInstance } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { useUserStore } from '@/store/user'
import dayjs, { type Dayjs } from 'dayjs'
import { getImgList, getTagNew } from '@/components/GlobleModal/NewUserInfoCollectForBModal/service'
import { UserTagReps } from '@/types'

// 常量定义
export const START_TIME = '2026-06-02 08:00:00'
export const AB_TEST_TYPES = {
  A: 'a',
  B: 'b'
} as const

export const TAG_LEVELS = {
  TYPE: 2,    // 二级标签（类型）
  STYLE: 3    // 三级标签（风格）
} as const

// Query Keys
export const QUERY_KEYS = {
  USER_TAG: 'userTag',
  GENERATE_IMAGE: 'generateImage'
} as const

// 类型定义
export type AbTestType = typeof AB_TEST_TYPES[keyof typeof AB_TEST_TYPES]

export interface UserTagData {
  type: UserTagReps[]
  style: UserTagReps[]
}

export interface GenerateImageParams {
  pageNo?: number
  pageSize?: number
}

export interface UseNewUserGuideAbTestReturn {
  // 状态 - 使用vue-query管理
  readonly userTag: Ref<UserTagData>
  readonly isLoadingUserTag: Readonly<Ref<boolean>>
  readonly isLoadingGenerateImage: Readonly<Ref<boolean>>
  readonly userTagError: Readonly<Ref<string | null>>
  readonly generateImageError: Readonly<Ref<string | null>>
  
  // 计算属性
  readonly abType: ComputedRef<AbTestType>
  readonly isAUser: ComputedRef<boolean>
  readonly isBUser: ComputedRef<boolean>
  readonly isValidAbTest: ComputedRef<boolean>
  readonly registrationDate: ComputedRef<Dayjs | null>
  readonly isNewUser: ComputedRef<boolean>
  
  // 方法
  getUserTag: () => Promise<UserTagData | null>
  hasGenerateImage: (params?: GenerateImageParams) => Promise<boolean>
  refreshUserTag: () => Promise<void>
  clearErrors: () => void
  getFormattedRegistrationTime: () => string
  
  // vue-query相关
  invalidateUserTag: () => Promise<void>
  prefetchUserTag: () => Promise<void>
}

export function useNewUserGuideAbTest(): UseNewUserGuideAbTestReturn {
  const userStore = useUserStore()
  
  // 检查是否在正确的 Vue 上下文中
  const instance = getCurrentInstance()
  const isInVueContext = !!instance

  // 本地状态管理（当无法使用vue-query时的降级方案）
  const localUserTag = ref<UserTagData>({
    type: [],
    style: []
  })
  const localIsLoading = ref(false)
  const localError = ref<string | null>(null)
  const generateImageError = ref<string | null>(null)
  const isLoadingGenerateImage = ref(false)

  // 用户标签查询函数
  const fetchUserTag = async (): Promise<UserTagData> => {
    try {
      const res = await getTagNew()
      
      if (!res?.data?.records) {
        throw new Error('获取用户标签数据格式错误')
      }

      // 2为二级标签（即类型），3为三级标签（即风格）
      const level2 = res.data.records.filter((item: UserTagReps) => item.level === TAG_LEVELS.TYPE)
      const level3 = res.data.records.filter((item: UserTagReps) => item.level === TAG_LEVELS.STYLE)
      
      return {
        type: level2,
        style: level3
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '获取用户标签失败'
      console.error('获取用户标签失败:', error)
      throw new Error(errorMessage)
    }
  }

  // 降级方案：手动管理数据
  const manualGetUserTag = async (): Promise<UserTagData | null> => {
    if (localIsLoading.value) {
      return null
    }

    localIsLoading.value = true
    localError.value = null

    try {
      const result = await fetchUserTag()
      localUserTag.value = result
      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '获取用户标签失败'
      localError.value = errorMessage
      console.error('获取用户标签失败:', error)
      return null
    } finally {
      localIsLoading.value = false
    }
  }

  // 尝试使用 vue-query（如果在正确的上下文中）
  let queryClient: any = null
  let userTagData: any = null
  let isLoadingUserTag: any = null
  let userTagQueryError: any = null
  let refetchUserTag: any = null

  try {
    if (isInVueContext) {
      queryClient = useQueryClient()
      
      const queryResult = useQuery({
        queryKey: [QUERY_KEYS.USER_TAG],
        queryFn: fetchUserTag,
        staleTime: 5 * 60 * 1000, // 5分钟数据保鲜时间
        gcTime: 10 * 60 * 1000,   // 10分钟垃圾回收时间
        retry: 2,
        retryDelay: 1000,
        enabled: false, // 初始不自动执行
        refetchOnWindowFocus: false,
        refetchOnReconnect: true
      })

      userTagData = queryResult.data
      isLoadingUserTag = queryResult.isLoading
      userTagQueryError = queryResult.error
      refetchUserTag = queryResult.refetch
    }
  } catch (error) {
    console.warn('Vue Query 初始化失败，使用降级方案:', error)
    // 降级到手动管理
  }

  // 计算属性 - AB测试类型
  const abType = computed((): AbTestType => {
    const { abtest, ctime } = userStore.userInfo

    /**
     * https://www.teambition.com/task/6865f25038c190b47d530004
     * 2025-07-03 全量B组
     */
    return AB_TEST_TYPES.B

    // 如果注册时间小于开始时间，直接返回 a
    if (dayjs(ctime || 0).isBefore(dayjs(START_TIME))) {
      return AB_TEST_TYPES.A
    }

    return (abtest as AbTestType) || AB_TEST_TYPES.A
  })

  // 计算属性 - 用户注册时间
  const registrationDate = computed((): Dayjs | null => {
    const { ctime } = userStore.userInfo
    return ctime ? dayjs(ctime) : null
  })

  // 计算属性 - 是否为新用户（注册时间在开始时间之后）
  const isNewUser = computed((): boolean => {
    const regDate = registrationDate.value
    return regDate ? regDate.isAfter(dayjs(START_TIME)) : false
  })

  // 计算属性 - 是否为有效的AB测试
  const isValidAbTest = computed((): boolean => {
    return isNewUser.value && !!userStore.userInfo.abtest
  })

  // 计算属性 - 判断是否为A组用户
  const isAUser = computed((): boolean => abType.value === AB_TEST_TYPES.A)

  // 计算属性 - 判断是否为B组用户
  const isBUser = computed((): boolean => abType.value === AB_TEST_TYPES.B)

  // 创建响应式的userTag，支持自动数据获取
  const userTag = computed<UserTagData>({
    get() {
      // 如果使用 vue-query 且有数据，返回 query 数据
      if (userTagData?.value) {
        return userTagData.value
      }
      
      // 如果有本地数据，返回本地数据
      if (localUserTag.value.type.length > 0 || localUserTag.value.style.length > 0) {
        return localUserTag.value
      }
      
      // 如果数据为空且不在加载中，自动触发请求
      const currentIsLoading = isLoadingUserTag?.value || localIsLoading.value
      if (!currentIsLoading) {
        // 使用 Promise.resolve() 避免在计算属性中直接触发副作用
        Promise.resolve().then(() => {
          if (refetchUserTag) {
            refetchUserTag()
          } else {
            manualGetUserTag()
          }
        })
      }
      
      // 返回默认值
      return {
        type: [],
        style: []
      }
    },
    set() {
      // userTag 是只读的，不允许直接设置
      console.warn('userTag 是只读属性，请使用 refreshUserTag() 方法更新数据')
    }
  })

  // 错误状态的响应式包装
  const userTagError = computed<string | null>(() => {
    return userTagQueryError?.value?.message || localError.value
  })

  // loading状态的响应式包装
  const finalIsLoadingUserTag = computed<boolean>(() => {
    return isLoadingUserTag?.value || localIsLoading.value
  })

  /**
   * 获取用户标签 - 兼容原有API
   * @returns Promise<UserTagData | null>
   */
  const getUserTag = async (): Promise<UserTagData | null> => {
    try {
      if (refetchUserTag) {
        const result = await refetchUserTag()
        return result.data || null
      } else {
        return await manualGetUserTag()
      }
    } catch (error) {
      console.error('获取用户标签失败:', error)
      return null
    }
  }

  /**
   * 判断用户是否有生图记录
   * @param params - 查询参数
   * @returns Promise<boolean>
   */
  const hasGenerateImage = async (params: GenerateImageParams = {}): Promise<boolean> => {
    if (isLoadingGenerateImage.value) {
      return false
    }

    isLoadingGenerateImage.value = true
    generateImageError.value = null

    try {
      const { data } = await getImgList({
        pageNo: 1,
        pageSize: 10,
        ...params
      })

      return data?.records?.length > 0
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '检查生图记录失败'
      generateImageError.value = errorMessage
      console.error('检查生图记录失败:', error)
      return false
    } finally {
      isLoadingGenerateImage.value = false
    }
  }

  /**
   * 刷新用户标签
   */
  const refreshUserTag = async (): Promise<void> => {
    if (refetchUserTag) {
      await refetchUserTag()
    } else {
      await manualGetUserTag()
    }
  }

  /**
   * 清除所有错误状态
   */
  const clearErrors = (): void => {
    generateImageError.value = null
    localError.value = null
    // vue-query的错误会在下次成功请求时自动清除
  }

  /**
   * 获取格式化的注册时间
   */
  const getFormattedRegistrationTime = (): string => {
    const regDate = registrationDate.value
    return regDate ? regDate.format('YYYY-MM-DD HH:mm:ss') : '未知'
  }

  /**
   * 使缓存失效并重新获取
   */
  const invalidateUserTag = async (): Promise<void> => {
    if (queryClient) {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER_TAG]
      })
    } else {
      await manualGetUserTag()
    }
  }

  /**
   * 预取用户标签数据
   */
  const prefetchUserTag = async (): Promise<void> => {
    if (queryClient) {
      await queryClient.prefetchQuery({
        queryKey: [QUERY_KEYS.USER_TAG],
        queryFn: fetchUserTag,
        staleTime: 5 * 60 * 1000
      })
    } else {
      await manualGetUserTag()
    }
  }

  // 监听userStore变化，自动重新获取数据
  if (isInVueContext) {
    watchEffect(() => {
      const { abtest, ctime } = userStore.userInfo
      // 当用户信息发生变化时，使缓存失效
      if (abtest || ctime) {
        invalidateUserTag()
      }
    })
  }

  return {
    // 状态
    userTag,
    isLoadingUserTag: readonly(finalIsLoadingUserTag),
    isLoadingGenerateImage: readonly(isLoadingGenerateImage),
    userTagError: readonly(userTagError),
    generateImageError: readonly(generateImageError),
    
    // 计算属性
    abType,
    isAUser,
    isBUser,
    isValidAbTest,
    registrationDate,
    isNewUser,
    
    // 方法
    getUserTag,
    hasGenerateImage,
    refreshUserTag,
    clearErrors,
    getFormattedRegistrationTime,
    
    // vue-query相关
    invalidateUserTag,
    prefetchUserTag
  }
}